import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { CloudSun, Wind, ExternalLink } from "lucide-react";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEATHER_APPS_IT = [
  { bg: "#0066B3", url: (v) => `https://www.3bmeteo.com/meteo/${encodeURIComponent(firstWord(v?.location_name))}`, urlNoVin: "https://www.3bmeteo.com/" },
  { bg: "#E63312", url: (v) => `https://www.ilmeteo.it/meteo/${encodeURIComponent(firstWord(v?.location_name).charAt(0).toUpperCase() + firstWord(v?.location_name).slice(1))}`, urlNoVin: "https://www.ilmeteo.it/" },
  { bg: "#1A4480", url: (v) => `https://www.meteoam.it/it/ricerca-localita?city=${encodeURIComponent(firstWord(v?.location_name))}`, urlNoVin: "https://www.meteoam.it/it/home" },
  { bg: "#3F4B5B", icon: Wind, url: (v) => `https://www.windy.com/?${v?.latitude},${v?.longitude},11`, urlNoVin: "https://www.windy.com/" },
];

// Fonti IT (3B Meteo, IlMeteo.it, Aeronautica Militare) sono legate a comuni italiani e non
// producono link utili per località francesi. Sostituite con Météo-France, Infoclimat e
// Vigilance Météo-France (portale ufficiale, senza bisogno di CAP/codice INSEE non disponibile
// nei dati del vigneto). Windy resta invariato: è basato su lat/lon, funziona ovunque.
const WEATHER_APPS_FR = [
  { bg: "#E63312", url: () => "https://meteofrance.com", urlNoVin: "https://meteofrance.com" },
  { bg: "#0066B3", url: () => "https://www.infoclimat.fr", urlNoVin: "https://www.infoclimat.fr" },
  { bg: "#1A4480", url: () => "https://vigilance.meteofrance.fr", urlNoVin: "https://vigilance.meteofrance.fr" },
  { bg: "#3F4B5B", icon: Wind, url: (v) => `https://www.windy.com/?${v?.latitude},${v?.longitude},11`, urlNoVin: "https://www.windy.com/" },
];

function cleanLoc(s) {
  if (!s) return "";
  // Extract first meaningful word/phrase before comma, then keep only first word (comune name)
  // "Monteodorisio, Chieti" -> "Monteodorisio"
  // "monteodorisio chieti"  -> "monteodorisio"
  // "GREVE IN CHIANTI, FI"  -> "greve in chianti" (not ideal but acceptable)
  const beforeComma = (s.split(",")[0] || "").trim();
  // Remove any trailing province abbreviations like "(CH)" or "CH"
  return beforeComma.replace(/\s*\([A-Za-z]{2}\)\s*$/g, "").trim().toLowerCase();
}

// Helper to extract just the first word (fallback when full name doesn't match)
function firstWord(s) {
  if (!s) return "";
  const cleaned = cleanLoc(s);
  return cleaned.split(/\s+/)[0] || cleaned;
}

export default function Meteo() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isFR = user?.country === "FR";
  const [vineyards, setVineyards] = useState([]);
  const [selected, setSelected] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/vineyards`, { withCredentials: true });
        setVineyards(r.data);
        if (r.data.length > 0) setSelected(r.data[0].id);
      } catch {}
    })();
  }, []);

  const current = vineyards.find((v) => v.id === selected);
  const appsMeta = isFR ? WEATHER_APPS_FR : WEATHER_APPS_IT;
  const appsText = t("meteoPage.apps", { returnObjects: true });
  const apps = appsMeta.map((app, i) => ({ ...app, ...appsText[i] }));

  return (
    <div className="min-h-screen pb-24" data-testid="meteo-page">
      <Header title={t("meteoPage.headerTitle")} subtitle={t("meteoPage.headerSubtitle")} />
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {/* Hero */}
        <section className="vs-card p-5"
          style={{ background: "var(--gradient-vine)", color: "white", borderColor: "transparent" }}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.18)", border: "2px solid rgba(255,255,255,0.3)" }}>
              <CloudSun size={24} color="white" strokeWidth={2.4} />
            </div>
            <div>
              <h2 className="heading text-xl font-extrabold">{t("meteoPage.heroTitle")}</h2>
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.85)" }}>
                {t("meteoPage.heroSubtitle")}
              </p>
            </div>
          </div>
        </section>

        {/* Vineyard selector */}
        {vineyards.length > 0 && (
          <section className="vs-card p-4" data-testid="meteo-vineyard-select">
            <div className="vs-label">{t("meteoPage.selectVineyardLabel")}</div>
            <div className="flex flex-wrap gap-2">
              {vineyards.map((v) => {
                const sel = v.id === selected;
                return (
                  <button key={v.id} onClick={() => setSelected(v.id)}
                    className="px-3 py-2 rounded-lg text-sm font-semibold"
                    style={{
                      background: sel ? "var(--primary)" : "var(--surface)",
                      color: sel ? "white" : "var(--text-primary)",
                      border: `2px solid ${sel ? "var(--primary)" : "var(--border)"}`,
                    }}
                    data-testid={`meteo-vineyard-${v.id}`}>
                    {v.name}
                  </button>
                );
              })}
            </div>
            {current && (
              <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
                📍 {current.location_name}
              </p>
            )}
          </section>
        )}

        {vineyards.length === 0 && (
          <section className="vs-card p-5 text-center">
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              {t("meteoPage.noVineyardText")}
            </p>
          </section>
        )}

        {/* Weather apps */}
        <section className="space-y-2" data-testid="meteo-apps">
          <div className="vs-label">{t("meteoPage.appsLabel")}</div>
          {apps.map((app, i) => {
            const Icon = app.icon || CloudSun;
            const url = current ? app.url(current) : app.urlNoVin;
            return (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                className="vs-card p-4 flex items-center gap-3 transition-transform hover:scale-[1.01]"
                style={{ borderLeft: `6px solid ${app.bg}`, textDecoration: "none" }}
                data-testid={`meteo-app-${i}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: app.bg }}>
                  <Icon size={22} color="white" strokeWidth={2.4} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="heading font-extrabold text-base" style={{ color: "var(--text-primary)" }}>
                    {app.name}
                  </div>
                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                    {app.desc}
                  </div>
                </div>
                <ExternalLink size={18} style={{ color: app.bg }} />
              </a>
            );
          })}
        </section>

        <p className="text-xs text-center" style={{ color: "var(--text-muted)" }}>
          {t("meteoPage.footerNote")}
        </p>
      </main>
      <BottomNav />
    </div>
  );
}
